import React, { useEffect, useMemo } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import { Container, ButtonPrimary } from 'components';
import { checkoutState, authenticationState, loadingState } from 'state/atoms';
import { CHECKOUT_SHIPPING_STEP } from 'utils/constants';
import { GET_ADDRESSES } from 'connectors/queries/account';
import { storageCheckoutSelector } from 'state/selectors';
import { defaultCheckoutState } from 'state/defaults';
import { Title, NavigationButtons } from 'components/checkout';
import { useNotifyError, useCheckoutStepsValidation } from 'utils/hooks';

import { AddressForm, ShippingMethodForm } from './subcomponents';
import { ADDRESS_SHIPPING_FIELDS } from './constants';
import { getValidationSchema } from './validationSchema';

const getFormInitialValues = (checkout) => {
  const defaultValues = {
    id: '',
    ...defaultCheckoutState.shipping.address,
    method: defaultCheckoutState.shipping.method,
    packingInstructions: defaultCheckoutState.shipping.packingInstructions,
  };

  const checkoutValues = {
    ...checkout.shipping.address,
    method: checkout.shipping.method,
    packingInstructions: checkout.shipping.packingInstructions,
  };

  return {
    ...defaultValues,
    ...checkoutValues,
  };
};

const CheckoutShipping = () => {
  const history = useHistory();

  const setCheckout = useSetRecoilState(storageCheckoutSelector);
  const setLoading = useSetRecoilState(loadingState);
  const { endCustomerAccountId } = useRecoilValue(authenticationState);

  const checkout = useRecoilValue(checkoutState);
  const schemaResolver = useMemo(() => yupResolver(getValidationSchema()), []);
  const formController = useForm({
    mode: 'onChange',
    defaultValues: getFormInitialValues(checkout),
    resolver: schemaResolver,
    shouldUnregister: false,
  });
  const validatePreviousSteps = useCheckoutStepsValidation(
    CHECKOUT_SHIPPING_STEP
  );

  const notifyAddressesError = useNotifyError({
    fallbackMessage: 'Unable to load some of the account addresses.',
  });

  const { data, loading, refetch } = useQuery(GET_ADDRESSES, {
    variables: { accountId: endCustomerAccountId },
    notifyOnNetworkStatusChange: true,
    onError: notifyAddressesError,
  });
  const accountAddresses = data ? data.getAccount.getAddresses : [];

  useEffect(() => {
    setLoading(loading);

    return () => {
      setLoading(false);
    };
  }, [loading, setLoading]);

  useEffect(() => {
    validatePreviousSteps().then((stepsAreValid) => {
      if (stepsAreValid && checkout.currentStep !== CHECKOUT_SHIPPING_STEP) {
        setCheckout({
          ...checkout,
          currentStep: CHECKOUT_SHIPPING_STEP,
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Run validation if form was filled before
    if (checkout.shipping.touched) {
      formController.trigger();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveCheckoutData = (formIsValid, touched = false) => {
    const address = formController.getValues(ADDRESS_SHIPPING_FIELDS);
    const methodFields = formController.getValues([
      'packingInstructions',
      'method',
    ]);

    setCheckout({
      ...checkout,
      shipping: {
        ...checkout.shipping,
        ...methodFields,
        address,
        touched,
      },
    });
  };

  const handleBackClick = () => {
    formController.trigger().then((valid) => {
      saveCheckoutData(valid, false);
      history.push('/cases/new/details');
    });
  };

  const handleNextClick = () => {
    formController.trigger().then((valid) => {
      if (valid) {
        saveCheckoutData(valid, true);
        history.push('/cases/new/checkout/billing');
      }
    });
  };

  return (
    <Container>
      <Title>Shipping Info</Title>
      <AddressForm
        formController={formController}
        accountAddresses={accountAddresses}
        refetch={refetch}
      />
      <ShippingMethodForm formController={formController} />
      <NavigationButtons>
        <ButtonPrimary onClick={handleBackClick}>BACK</ButtonPrimary>
        <ButtonPrimary onClick={handleNextClick}>NEXT</ButtonPrimary>
      </NavigationButtons>
    </Container>
  );
};

export { CheckoutShipping };
